import {useId, type ReactNode} from 'react';
import {AlertCircle, Check, ListChecks, Tag, Undo2, X} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Spinner,
  Textarea,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {PageHeaderActions} from '@/components/layout/PageHeader';
import {SensitiveMediaToggle} from '@/components/shared/SensitiveMedia';
import {ExistingSubmissionBadge} from '@/components/channels/ExistingSubmissionBadge';
import {FlagIcon, FlagPicker} from '@/components/flags/Flag';
import {type usePendingFlags} from '@/components/submissions/usePendingFlags';
import {type Submission} from '@/api/submissions';
import {type FlagColor} from '@/api/flags';
import {toneBadge} from '@/lib/status';
import {cn} from '@/lib/utils';

type PendingFlags = ReturnType<typeof usePendingFlags>;

// The chrome every /submission page shares: the blur toggle in the page header and the centred
// column the form and its resolved rows sit in. Pages differ only in what they resolve, so this is
// where the common visual identity lives.
export function SubmissionPageShell({children}: Readonly<{children: ReactNode}>) {
  return (
    <div className="bg-background text-foreground h-full w-full overflow-y-auto">
      <PageHeaderActions>
        <SensitiveMediaToggle />
      </PageHeaderActions>

      <div className="mx-auto flex max-w-4xl flex-col gap-7 px-6 py-6">{children}</div>
    </div>
  );
}

type SubmissionInputSectionProps = Readonly<{
  title: string;
  description: string;
  platformLabel: string;
  platformIcon: ReactNode;
  inputLabel: string;
  placeholder: string;
  idleHint: string;
  value: string;
  onValueChange: (value: string) => void;
  hasLines: boolean;
  resolvedCount: number;
  unrecognisedCount: number;
  pendingFlags: PendingFlags;
  classifiableKeys: string[];
  flagAllSubject: string;
  onClassifyAll: () => void;
}>;

// The paste form: heading, fixed target-platform toggle, one-per-line textarea, and the
// Flag all / Classify all actions. Per the design it is a Card: the heading and fields group in the
// content, and the button row sits right-aligned in the footer.
export function SubmissionInputSection(props: SubmissionInputSectionProps) {
  const inputId = useId();
  const count = props.classifiableKeys.length;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="font-heading text-sm font-medium">{props.title}</h2>
          <p className="text-muted-foreground text-xs">{props.description}</p>
        </div>
        <FieldGroup>
          <Field>
            <FieldLabel>Target platform</FieldLabel>
            <ToggleGroup
              variant="outline"
              spacing={0}
              size="sm"
              aria-label="Target platform"
              value={[props.platformLabel]}>
              <ToggleGroupItem value={props.platformLabel}>
                {props.platformIcon}
                {props.platformLabel}
              </ToggleGroupItem>
            </ToggleGroup>
            <FieldDescription>Fixed per deployment</FieldDescription>
          </Field>

          <Field>
            <FieldLabel htmlFor={inputId}>
              {props.inputLabel}
              <span aria-hidden="true" className="text-destructive-ink">
                *
              </span>
            </FieldLabel>
            <Textarea
              required
              id={inputId}
              rows={4}
              placeholder={props.placeholder}
              value={props.value}
              className="min-h-24 resize-none"
              onChange={(e) => {
                props.onValueChange(e.target.value);
              }}
            />
            <FieldDescription>
              {props.hasLines
                ? `${props.resolvedCount} resolved · ${props.unrecognisedCount} unrecognised`
                : props.idleHint}
            </FieldDescription>
          </Field>
        </FieldGroup>
      </CardContent>
      <CardFooter className="justify-end gap-2">
        <FlagPicker
          variant="button"
          label="Flag all"
          subject={props.flagAllSubject}
          disabled={count === 0}
          value={props.pendingFlags.sharedBy(props.classifiableKeys)}
          clearable={props.pendingFlags.anyFlagged(props.classifiableKeys)}
          onChange={(color) => {
            props.pendingFlags.setAll(props.classifiableKeys, color);
          }}
        />
        <Button type="button" size="sm" disabled={count === 0} onClick={props.onClassifyAll}>
          <ListChecks />
          Classify all
          {count > 0 ? ` (${count})` : ''}
        </Button>
      </CardFooter>
    </Card>
  );
}

type ResolvedSectionProps = Readonly<{
  title: string;
  summary: string;
  actions?: ReactNode;
  children: ReactNode;
}>;

// Heading + optional controls above the list of resolved rows.
export function ResolvedSection({title, summary, actions, children}: ResolvedSectionProps) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading text-sm font-medium">{title}</h3>
          <p className="text-muted-foreground text-xs">{summary}</p>
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

type ResolvedRowProps = Readonly<{
  raw: string;
  loading: boolean;
  error: string | null;
  existingSubmissions: Submission[];
  // Set aside by the analyst: still shown, but greyed out so it reads as out of this batch.
  dismissed?: boolean;
  children?: ReactNode;
}>;

// One pasted line: spinner while resolving, an error alert if it failed, the prior-submission
// badge, then the platform's own status card as children.
export function ResolvedRow({
  raw,
  loading,
  error,
  existingSubmissions,
  dismissed = false,
  children
}: ResolvedRowProps) {
  return (
    <div className={cn('flex flex-col gap-2', dismissed && 'opacity-50 grayscale')}>
      {loading ? (
        <Card size="sm">
          <CardContent className="text-muted-foreground flex items-center gap-2">
            <Spinner />
            <span className="font-mono text-xs">{raw}</span>
          </CardContent>
        </Card>
      ) : null}

      {!loading && error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle className="font-mono font-normal break-all">{raw}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {!loading && existingSubmissions.length > 0 ? (
        <ExistingSubmissionBadge submissions={existingSubmissions} />
      ) : null}

      {loading ? null : children}
    </div>
  );
}

type RowActionsProps = Readonly<{
  submitted: boolean;
  flag: FlagColor | null;
  note: string | null;
  subject: string;
  onFlag: (color: FlagColor | null, note?: string | null) => void;
  onNote: (note: string | null) => void;
  onClassify: () => void;
  dismissed?: boolean;
  onDismiss: () => void;
  onRestore: () => void;
}>;

// A row's own actions, rendered inside its status card's footer.
export function RowActions({
  submitted,
  flag,
  note,
  subject,
  onFlag,
  onNote,
  onClassify,
  dismissed = false,
  onDismiss,
  onRestore
}: RowActionsProps) {
  if (submitted) {
    return (
      <>
        {flag ? <FlagIcon color={flag} /> : null}
        <Badge {...toneBadge('success')}>
          <Check />
          Submitted
        </Badge>
      </>
    );
  }

  if (dismissed) {
    return (
      <>
        <Badge variant="outline">Dismissed</Badge>
        <Button type="button" size="sm" variant="outline" onClick={onRestore}>
          <Undo2 />
          Restore
        </Button>
      </>
    );
  }

  return (
    <>
      <Button type="button" size="sm" variant="ghost" onClick={onDismiss}>
        <X />
        Dismiss
      </Button>
      <FlagPicker
        variant="button"
        label="Flag"
        subject={subject}
        value={flag}
        note={note}
        onChange={onFlag}
        onNoteChange={onNote}
      />
      <Button type="button" size="sm" onClick={onClassify}>
        <Tag />
        Classify
      </Button>
    </>
  );
}
